<template>
  <div class="chat-workspace">
    <!-- 欢迎区域 -->
    <div class="welcome-section" v-if="messages.length === 0">
      <div class="welcome-icon">{{ currentTool.icon }}</div>
      <h2 class="welcome-title">{{ currentTool.welcomeTitle }}</h2>
      <p class="welcome-desc">{{ currentTool.welcomeDesc }}</p>
      <div class="quick-actions">
        <button
          v-for="(action, index) in currentTool.quickActions"
          :key="index"
          class="quick-action-btn"
          @click="$emit('quick-action', action)"
        >
          {{ action }}
        </button>
      </div>
    </div>

    <!-- 消息列表 -->
    <div class="messages-container" ref="messagesContainer">
      <div
        v-for="(msg, index) in messages"
        :key="index"
        class="message"
        :class="{ 'message-user': msg.role === 'user', 'message-ai': msg.role === 'assistant' }"
      >
        <div class="message-avatar">
          <span v-if="msg.role === 'user'">👤</span>
          <span v-else>{{ currentTool.icon }}</span>
        </div>
        <div class="message-content">
          <div class="message-role">
            {{ msg.role === 'user' ? '你' : currentTool.title }}
          </div>
          <div class="message-text">{{ msg.content }}</div>
        </div>
      </div>

      <!-- 加载中 -->
      <div class="message message-ai" v-if="isLoading">
        <div class="message-avatar">{{ currentTool.icon }}</div>
        <div class="message-content">
          <div class="message-role">{{ currentTool.title }}</div>
          <div class="typing-indicator">
            <span></span><span></span><span></span>
          </div>
        </div>
      </div>
    </div>

    <!-- 输入区域 -->
    <div class="input-section">
      <div class="input-wrapper">
        <textarea
          v-model="localInput"
          class="message-input"
          placeholder="输入你的问题... (Shift + Enter 换行)"
          @keydown.enter.exact.prevent="$emit('send-message')"
          @keydown.shift.enter.exact="handleShiftEnter"
          :disabled="isLoading"
          rows="1"
          ref="inputRef"
          @input="onInput"
        ></textarea>
        <button
          class="send-btn"
          @click="$emit('send-message')"
          :disabled="!localInput.trim() || isLoading"
        >
          <span v-if="!isLoading">发送</span>
          <span v-else>发送中...</span>
        </button>
      </div>
      <div class="input-tips">按下 Enter 发送，Shift + Enter 换行</div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ChatWorkspace',
  props: {
    currentTool: {
      type: Object,
      required: true
    },
    messages: {
      type: Array,
      default: () => []
    },
    isLoading: {
      type: Boolean,
      default: false
    },
    userInput: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      localInput: ''
    }
  },
  watch: {
    userInput(val) {
      this.localInput = val
    }
  },
  mounted() {
    this.localInput = this.userInput
  },
  methods: {
    onInput() {
      this.$emit('input-change', this.localInput)
      this.$nextTick(() => {
        this.adjustTextareaHeight()
      })
    },
    handleShiftEnter() {
      return true
    },
    adjustTextareaHeight() {
      const textarea = this.$refs.inputRef
      if (textarea) {
        textarea.style.height = 'auto'
        textarea.style.height = Math.min(textarea.scrollHeight, 150) + 'px'
      }
    },
    scrollToBottom() {
      const container = this.$refs.messagesContainer
      if (container) {
        container.scrollTop = container.scrollHeight
        this.$emit('scroll-bottom')
      }
    }
  }
}
</script>

<style scoped>
.chat-workspace {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.welcome-section {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  text-align: center;
}
.welcome-icon {
  font-size: 64px;
  margin-bottom: 24px;
}
.welcome-title {
  font-size: 32px;
  color: #1a1a2e;
  margin-bottom: 16px;
}
.welcome-desc {
  font-size: 16px;
  color: #666;
  max-width: 600px;
  line-height: 1.6;
  margin-bottom: 32px;
}
.quick-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  justify-content: center;
  max-width: 800px;
}
.quick-action-btn {
  background: white;
  border: 1px solid #e8e8e8;
  padding: 12px 24px;
  border-radius: 12px;
  cursor: pointer;
  font-size: 14px;
  color: #1a1a2e;
  transition: all 0.3s ease;
}
.quick-action-btn:hover {
  border-color: #667eea;
  color: #667eea;
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.15);
}
.messages-container {
  flex: 1;
  overflow-y: auto;
  padding: 24px;
  padding-bottom: 0;
}
.message {
  display: flex;
  gap: 12px;
  margin-bottom: 24px;
  animation: fadeIn 0.3s ease;
}
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
.message-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  flex-shrink: 0;
}
.message-user .message-avatar {
  background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
}
.message-content {
  flex: 1;
  max-width: 70%;
}
.message-role {
  font-size: 12px;
  color: #999;
  margin-bottom: 6px;
  font-weight: 500;
}
.message-text {
  background: white;
  padding: 12px 16px;
  border-radius: 12px;
  line-height: 1.6;
  color: #1a1a2e;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  white-space: pre-wrap;
  word-break: break-word;
}
.message-user .message-text {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
}
.typing-indicator {
  display: flex;
  gap: 4px;
  padding: 12px 16px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}
.typing-indicator span {
  width: 8px;
  height: 8px;
  background: #667eea;
  border-radius: 50%;
  animation: typing 1.4s infinite;
}
.typing-indicator span:nth-child(2) {
  animation-delay: 0.2s;
}
.typing-indicator span:nth-child(3) {
  animation-delay: 0.4s;
}
@keyframes typing {
  0%,
  60%,
  100% {
    transform: translateY(0);
    opacity: 0.7;
  }
  30% {
    transform: translateY(-10px);
    opacity: 1;
  }
}
.input-section {
  padding: 20px 24px;
  background: white;
  border-top: 1px solid #e8e8e8;
}
.input-wrapper {
  display: flex;
  gap: 12px;
  align-items: flex-end;
}
.message-input {
  flex: 1;
  border: 2px solid #e8e8e8;
  border-radius: 12px;
  padding: 12px 16px;
  font-size: 14px;
  font-family: inherit;
  resize: none;
  outline: none;
  transition: all 0.3s ease;
  min-height: 44px;
  max-height: 150px;
}
.message-input:focus {
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}
.message-input:disabled {
  background: #f5f7fa;
  cursor: not-allowed;
}
.send-btn {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 12px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.3s ease;
  min-width: 80px;
}
.send-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
}
.send-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.input-tips {
  font-size: 12px;
  color: #999;
  margin-top: 8px;
  text-align: center;
}
@media (max-width: 768px) {
  .welcome-title {
    font-size: 24px;
  }
  .welcome-desc {
    font-size: 14px;
  }
  .quick-actions {
    flex-direction: column;
    width: 100%;
  }
  .message-content {
    max-width: 85%;
  }
  .messages-container {
    padding: 16px;
  }
  .input-section {
    padding: 12px 16px;
  }
}
</style>
